// @ts-nocheck
import React, { useState, useMemo } from "react";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import {
  FileSpreadsheet,
  TrendingUp,
  Factory,
  Warehouse,
  CreditCard,
  Printer,
  Loader2,
  Search,
  CheckCircle2,
  AlertTriangle,
  Clock,
  DollarSign,
  Package,
  Layers,
  Gauge,
  BarChart3,
  PieChart as PieIcon,
  ShoppingCart,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  exportSalesReport,
  exportProductionReport,
  exportInventoryNXTReport,
  exportARAgingReport,
  formatCurrencyVND,
} from "@/lib/excelReportEngine";
import { ZIndexView, useZIndexView, type ZIndexViewType } from "@/components/shared/ZIndexView";
import { KpiStatCard, formatShortVnd } from "@/components/shared/KpiStatCard";
import { ERPPagination } from "@/components/shared/ERPPagination";
import { RComboChart, RDonutChart, fmtShort } from "@/pages/dashboard/components/Charts";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const CHART_PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#64748b"];

export default function ReportsDashboardPage() {
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const zIndex = useZIndexView();

  const handleRowClick = (e: React.MouseEvent, type: ZIndexViewType, id?: string) => {
    if (!id) return;
    if (window.getSelection()?.toString().trim().length) return;
    zIndex.openView(type, id);
  };

  // Auto-detect initial tab based on route
  const initialTab = useMemo(() => {
    if (pathname.includes("/sales")) return "sales";
    if (pathname.includes("/production")) return "production";
    if (pathname.includes("/inventory")) return "inventory";
    if (pathname.includes("/ar-aging")) return "ar_aging";
    return "sales";
  }, [pathname]);

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const { headerSearch } = useHeaderSearch();
  const searchTerm = headerSearch || "";
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("ALL");

  // Pagination states for 4 tabs
  const [salesPage, setSalesPage] = useState(1);
  const [salesPageSize, setSalesPageSize] = usePageSizeState(25);

  const [prodPage, setProdPage] = useState(1);
  const [prodPageSize, setProdPageSize] = usePageSizeState(25);

  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = usePageSizeState(25);

  const [arPage, setArPage] = useState(1);
  const [arPageSize, setArPageSize] = usePageSizeState(25);

  // Date Filter Hook
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter("thisMonth");

  // Period label for export file
  const periodText = useMemo(() => {
    const dmy = (d?: string) => (d ? d.split("-").reverse().join("/") : "");
    if (period === "today") return `Hôm nay (${dmy(dateParams.to)})`;
    if (period === "week") return "Tuần này";
    if (period === "month") return `Tháng ${dmy(dateParams.to).slice(3)}`;
    if (period === "quarter") return "Quý này";
    if (period === "year") return `Năm ${(dateParams.to || "").slice(0, 4)}`;
    if (dateParams.from && dateParams.to) {
      return `Từ ${dmy(dateParams.from)} đến ${dmy(dateParams.to)}`;
    }
    return "Toàn thời gian";
  }, [period, dateParams]);

  // 1. QUERY SALES DATA (Server-side Aggregation & PoP)
  const { data: salesReport, isLoading: isLoadingSales } = useQuery({
    queryKey: ["reports-getSalesData", period, dateParams, salesPage, salesPageSize, searchTerm],
    queryFn: () =>
      trpcClient.reports.getSalesData.query({
        period,
        startDate: dateParams.from,
        endDate: dateParams.to,
        page: salesPage,
        limit: salesPageSize,
        search: searchTerm || undefined,
      }),
    enabled: activeTab === "sales",
  });

  // 2. QUERY PRODUCTION DATA (Server-side Aggregation & PoP)
  const { data: prodReport, isLoading: isLoadingProd } = useQuery({
    queryKey: ["reports-getProductionData", period, dateParams, prodPage, prodPageSize, searchTerm],
    queryFn: () =>
      trpcClient.reports.getProductionData.query({
        period,
        startDate: dateParams.from,
        endDate: dateParams.to,
        page: prodPage,
        limit: prodPageSize,
        search: searchTerm || undefined,
      }),
    enabled: activeTab === "production",
  });

  // 2.5 QUERY DYNAMIC WAREHOUSES
  const { data: warehouseData } = useQuery({
    queryKey: ["warehouses-list-lookup"],
    queryFn: async () => {
      const res = await trpcClient.warehouses.list.query({ limit: 100 });
      return (res as any)?.items || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // 3. QUERY INVENTORY DATA (Server-side Aggregation & PoP)
  const { data: invReport, isLoading: isLoadingInv } = useQuery({
    queryKey: ["reports-getInventoryData", period, dateParams, selectedWarehouse, invPage, invPageSize, searchTerm],
    queryFn: () =>
      trpcClient.reports.getInventoryData.query({
        period,
        startDate: dateParams.from,
        endDate: dateParams.to,
        warehouseId: selectedWarehouse === "ALL" ? undefined : selectedWarehouse,
        page: invPage,
        limit: invPageSize,
        search: searchTerm || undefined,
      }),
    enabled: activeTab === "inventory",
  });

  // 4. QUERY AR AGING REPORT DATA (Server-side Aggregation)
  const { data: arReport, isLoading: isLoadingAR } = useQuery({
    queryKey: ["reports-getARAgingData"],
    queryFn: () => trpcClient.reports.getARAgingData.query({}),
    enabled: activeTab === "ar_aging",
  });

  // 5. QUERY PRODUCTION LOGS (SHOPFLOOR)
  const { data: logsRes, isLoading: isLoadingLogs } = useQuery({
    queryKey: ["reports-shopfloor-logs", dateParams],
    queryFn: () =>
      trpcClient.productionLogs.list.query({
        limit: 500,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
      }),
    enabled: activeTab === "shopfloor",
  });

  // Summary shortcuts
  const salesSummary = salesReport?.summary || {
    totalRevenue: 0,
    prevRevenue: 0,
    totalOrders: 0,
    prevOrders: 0,
    totalPaid: 0,
    totalDebt: 0,
  };
  const salesOrders = salesReport?.orders || [];
  const salesTotal = salesReport?.total || 0;

  const prodSummary = prodReport?.summary || {
    totalPlan: 0,
    totalProduced: 0,
    totalWaste: 0,
    yieldRate: 100,
    scrapRate: 0,
    totalPOs: 0,
  };
  const prodPOs = prodReport?.pos || [];
  const prodTotal = prodReport?.total || 0;

  const invSummary = invReport?.summary || {
    totalItems: 0,
    totalInventoryValue: 0,
    prevInventoryValue: 0,
    totalImportQty: 0,
    totalExportQty: 0,
    totalStockQty: 0,
  };
  // Bảng kê NXT chi tiết: tính ở máy chủ theo kho + mặt hàng + số lô (cùng nguồn với Kho > Báo cáo NXT, FB-JMLIL2)
  const nxtInput = {
    startDate: dateParams.from,
    endDate: dateParams.to,
    itemType: "ALL",
    warehouseId: selectedWarehouse === "ALL" ? undefined : selectedWarehouse,
  };
  const { data: nxtReport } = useQuery({
    queryKey: ["inventory", "reportNXT", "reports-dashboard", nxtInput],
    queryFn: () => trpcClient.inventory.reportNXT.query(nxtInput),
    enabled: activeTab === "inventory",
  });
  const toNxtRows = (raw: any[]) => {
    const term = searchTerm.trim().toLowerCase();
    return (raw || [])
      .filter((r: any) =>
        !term ||
        [r.itemCode, r.itemName, r.warehouseName].some((v) => String(v || "").toLowerCase().includes(term))
      )
      .map((r: any) => ({
        ...r,
        openingQty: Number(r.openingBalance || 0),
        inQty: Number(r.inQuantity || 0),
        outQty: Number(r.outQuantity || 0),
        closingQty: Number(r.closingBalance || 0),
        valuation: Number(r.closingBalance || 0) * Number(r.material?.unitPrice || 0),
      }));
  };
  const nxtRows = useMemo(() => toNxtRows((nxtReport as any)?.data), [nxtReport, searchTerm]);
  const invTotal = nxtRows.length;
  const invItems = nxtRows.slice((invPage - 1) * invPageSize, invPage * invPageSize);

  const arSummary = arReport?.summary || {
    totalDebt: 0,
    inTermDebt: 0,
    overdueDebt: 0,
    overdueRate: 0,
    totalCustomersWithDebt: 0,
  };
  const arItems = useMemo(() => {
    const raw = arReport?.items || [];
    if (!searchTerm) return raw;
    const term = searchTerm.toLowerCase();
    return raw.filter(
      (c: any) =>
        (c.customerCode && c.customerCode.toLowerCase().includes(term)) ||
        (c.customerName && c.customerName.toLowerCase().includes(term))
    );
  }, [arReport?.items, searchTerm]);

  // --- EXPORT HANDLERS ---
  const handleExportSales = async () => {
    try {
      toast.loading("Đang xuất Báo cáo Doanh thu...");
      await exportSalesReport({
        periodText,
        orders: salesOrders,
        summary: salesSummary,
      });
      toast.dismiss();
      toast.success("Xuất file Excel Báo cáo Doanh thu thành công!");
    } catch (err: any) {
      toast.dismiss();
      toast.error(`Lỗi xuất Excel: ${err.message}`);
    }
  };

  const handleExportProduction = async () => {
    try {
      toast.loading("Đang xuất Báo cáo Sản xuất...");
      await exportProductionReport({
        periodText,
        pos: prodPOs,
        summary: prodSummary,
      });
      toast.dismiss();
      toast.success("Xuất file Excel Báo cáo Tiến độ LSX thành công!");
    } catch (err: any) {
      toast.dismiss();
      toast.error(`Lỗi xuất Excel: ${err.message}`);
    }
  };

  const handleExportInventory = async () => {
    try {
      toast.loading("Đang xuất Sổ kho NXT...");
      const activeWh = warehouseData?.find(
        (w: any) => w.id === selectedWarehouse || w.code === selectedWarehouse
      );
      // Lấy lại toàn bộ dòng từ máy chủ, không chỉ trang đang xem
      const fresh: any = await trpcClient.inventory.reportNXT.query(nxtInput);
      await exportInventoryNXTReport({
        periodText,
        warehouseName:
          selectedWarehouse === "ALL"
            ? "Tất cả kho"
            : activeWh
              ? `[${activeWh.code}] ${activeWh.name}`
              : selectedWarehouse,
        items: toNxtRows(fresh?.data),
      });
      toast.dismiss();
      toast.success("Xuất file Excel Sổ kho NXT thành công!");
    } catch (err: any) {
      toast.dismiss();
      toast.error(`Lỗi xuất Excel: ${err.message}`);
    }
  };

  const handleExportARAging = async () => {
    try {
      toast.loading("Đang xuất Báo cáo Tuổi nợ...");
      await exportARAgingReport({
        agingData: arItems,
        summary: arSummary,
      });
      toast.dismiss();
      toast.success("Xuất file Excel Báo cáo Tuổi nợ AR thành công!");
    } catch (err: any) {
      toast.dismiss();
      toast.error(`Lỗi xuất Excel: ${err.message}`);
    }
  };

  return (
    <div className="space-y-3 pb-6">
      {/* Thanh điều khiển chuẩn 2 hàng: hàng 1 tabs báo cáo (cuộn ngang trên mobile, không gãy lưới),
          hàng 2 bộ lọc kỳ + bộ lọc riêng của tab. */}
      <Tabs
        value={activeTab}
        onValueChange={(val) => {
          setActiveTab(val);
        }}
        className="w-full space-y-3"
      >
        <div className="bg-card border border-border rounded-xl shadow-2xs">
          <div className="overflow-x-auto hide-scrollbar border-b border-border px-2 pt-2">
            <TabsList className="mb-2">
              <TabsTrigger value="sales" className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> Doanh Thu Bán Hàng
              </TabsTrigger>
              <TabsTrigger value="production" className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                <Factory className="w-3.5 h-3.5 text-blue-600" /> Tiến Độ LSX
              </TabsTrigger>
              <TabsTrigger value="inventory" className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                <Warehouse className="w-3.5 h-3.5 text-amber-600" /> Sổ Kho NXT
              </TabsTrigger>
              <TabsTrigger value="ar_aging" className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                <CreditCard className="w-3.5 h-3.5 text-purple-600" /> Tuổi Nợ Khách Hàng
              </TabsTrigger>
              <TabsTrigger value="shopfloor" className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                <Printer className="w-3.5 h-3.5 text-slate-600" /> Nhật Ký Xưởng
              </TabsTrigger>
            </TabsList>
          </div>
          {/* Hàng lọc chuẩn: bộ lọc thời gian luôn đứng đầu bên trái */}
          <div className="erp-list-filter-row flex items-center gap-2 px-2.5 py-2 overflow-x-auto hide-scrollbar flex-nowrap">
            <div className="date-filter-pos0 flex items-center gap-1.5 shrink-0">
              <DatePeriodFilter
                period={period}
                setPeriod={setPeriod}
                customFrom={customFrom}
                setCustomFrom={setCustomFrom}
                customTo={customTo}
                setCustomTo={setCustomTo}
              />
            </div>
            {activeTab === "inventory" && (
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger className="w-[180px] h-8 text-xs bg-card">
                  <SelectValue placeholder="Chọn kho hàng" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-xs">Tất cả kho hàng</SelectItem>
                  {warehouseData?.map((w: any) => (
                    <SelectItem key={w.id} value={w.id} className="text-xs">
                      {w.name} ({w.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {headerSearch && (
              <Badge variant="secondary" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                Tìm kiếm: "{headerSearch}"
              </Badge>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 1: BÁO CÁO DOANH THU BÁN HÀNG */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="sales" className="space-y-3 mt-3">
          {/* Cụm 4 Thẻ KPI Chuẩn PoP */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiStatCard
              title="Tổng doanh số"
              value={salesSummary.totalRevenue}
              prevValue={salesSummary.prevRevenue}
              deltaPct={salesSummary.revenueGrowth?.pct}
              diff={salesSummary.revenueGrowth?.diff}
              formatType="currencyShort"
              colorVariant="emerald"
              icon={DollarSign}
              direction="positive"
              isLoading={isLoadingSales}
              testId="sales-kpi-revenue"
            />

            <KpiStatCard
              title="Đơn hàng ghi nhận"
              value={salesSummary.totalOrders}
              prevValue={salesSummary.prevOrders}
              deltaPct={salesSummary.orderGrowth?.pct}
              diff={salesSummary.orderGrowth?.diff}
              unit="đơn"
              formatType="number"
              colorVariant="blue"
              icon={ShoppingCart}
              direction="positive"
              isLoading={isLoadingSales}
              testId="sales-kpi-orders"
            />

            <KpiStatCard
              title="Thực thu đã nhận"
              value={salesSummary.totalPaid}
              formatType="currencyShort"
              colorVariant="teal"
              icon={CheckCircle2}
              badgeText="Tiền mặt & NH"
              direction="positive"
              isLoading={isLoadingSales}
              testId="sales-kpi-paid"
            />

            <KpiStatCard
              title="Công nợ phát sinh"
              value={salesSummary.totalDebt}
              formatType="currencyShort"
              colorVariant="amber"
              icon={CreditCard}
              badgeText="Theo dõi thu hồi"
              direction="inverse"
              isLoading={isLoadingSales}
              testId="sales-kpi-debt"
            />
          </div>

          {/* 2 Biểu Đồ Trực Quan Xu Hướng */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="lg:col-span-2 shadow-2xs border-border bg-card">
              <CardHeader className="py-2.5 px-3.5 border-b border-border">
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-blue-500" />
                  Xu Hướng Doanh Thu 6 Tháng Gần Nhất
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3.5">
                {isLoadingSales ? (
                  <div className="h-[220px] flex items-center justify-center text-muted-foreground text-xs">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Đang tổng hợp biểu đồ...
                  </div>
                ) : (
                  <RComboChart data={salesReport?.monthlyTrend || []} height={220} />
                )}
              </CardContent>
            </Card>

            <Card className="shadow-2xs border-border bg-card flex flex-col">
              <CardHeader className="py-2.5 px-3.5 border-b border-border">
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                  Top Khách Hàng Doanh Số
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3.5 flex-1 flex flex-col justify-center">
                {isLoadingSales ? (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground text-xs">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Đang tải...
                  </div>
                ) : (salesReport?.topCustomers || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8 font-medium">
                    Chưa có phát sinh trong kỳ
                  </p>
                ) : (
                  <div className="space-y-2.5">
                    {salesReport.topCustomers.map((cust: any, idx: number) => {
                      const maxRev = salesReport.topCustomers[0]?.revenue || 1;
                      const pct = Math.round((cust.revenue / maxRev) * 100);
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold text-foreground truncate max-w-[150px]">
                              {idx + 1}. {cust.name}
                            </span>
                            <span className="font-mono font-bold text-foreground">
                              {formatShortVnd(cust.revenue)}
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bảng Kê Chi Tiết Có Phân Trang ERPPagination */}
          <Card className="shadow-2xs border-border bg-card overflow-hidden">
            <CardHeader className="flex flex-row justify-between items-center py-2.5 px-3.5 border-b border-border bg-muted/20">
              <div>
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                  Chi Tiết Đơn Hàng ({salesTotal.toLocaleString()} đơn)
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={handleExportSales}
                disabled={salesOrders.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-7 text-xs font-semibold shadow-2xs"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> Xuất Excel
              </Button>
            </CardHeader>

            <CardContent className="p-0">
              {isLoadingSales ? (
                <div className="flex justify-center items-center py-16 gap-2 text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin" /> Đang tải dữ liệu doanh thu...
                </div>
              ) : salesOrders.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-xs">
                  Không tìm thấy đơn hàng nào trong khoảng thời gian đã chọn.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/40">
                      <TableRow>
                        <TableHead className="w-12 text-center text-xs">STT</TableHead>
                        <TableHead className="text-xs">Mã Đơn</TableHead>
                        <TableHead className="text-xs">PO Khách</TableHead>
                        <TableHead className="text-xs">Khách Hàng</TableHead>
                        <TableHead className="text-xs text-center">Ngày Đặt</TableHead>
                        <TableHead className="text-xs text-center">Hẹn Giao</TableHead>
                        <TableHead className="text-xs text-right">Tổng Tiền (VND)</TableHead>
                        <TableHead className="text-xs text-right">Đã Thu (VND)</TableHead>
                        <TableHead className="text-xs text-right">Còn Nợ (VND)</TableHead>
                        <TableHead className="text-xs text-center">Trạng Thái</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesOrders.map((o: any, idx: number) => {
                        const total = Number(o.totalAmount || 0);
                        const paid = Number(o.depositAmount || o.paidAmount || 0);
                        const debt = Math.max(0, total - paid);
                        const stt = (salesPage - 1) * salesPageSize + idx + 1;

                        return (
                          <TableRow
                            key={o.id}
                            className="hover:bg-primary/5 cursor-pointer text-xs transition-colors"
                            onClick={(e) => handleRowClick(e, "order", o.id)}
                          >
                            <TableCell className="text-center font-mono text-muted-foreground">
                              {stt}
                            </TableCell>
                            <TableCell className="font-mono font-bold text-foreground">
                              {o.orderId || o.code}
                            </TableCell>
                            <TableCell className="text-muted-foreground font-mono">
                              {o.poNumber || "-"}
                            </TableCell>
                            <TableCell className="font-medium text-foreground">
                              {o.customer?.shortName || o.customer?.name || "Khách vãng lai"}
                            </TableCell>
                            <TableCell className="text-center text-muted-foreground">
                              {o.orderDate ? format(new Date(o.orderDate), "dd/MM/yyyy") : "-"}
                            </TableCell>
                            <TableCell className="text-center text-muted-foreground">
                              {o.deliveryDate ? format(new Date(o.deliveryDate), "dd/MM/yyyy") : "-"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-foreground">
                              {formatCurrencyVND(total)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-emerald-600">
                              {formatCurrencyVND(paid)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-rose-600">
                              {debt > 0 ? formatCurrencyVND(debt) : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-[10px]">
                                {o.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Phân Trang ERPPagination */}
              <ERPPagination
                currentPage={salesPage}
                totalPages={Math.max(1, Math.ceil(salesTotal / salesPageSize))}
                totalItems={salesTotal}
                pageSize={salesPageSize}
                onPageChange={setSalesPage}
                onPageSizeChange={setSalesPageSize}
                unitLabel="đơn hàng"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 2: TIẾN ĐỘ LỆNH SẢN XUẤT */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="production" className="space-y-3 mt-3">
          {/* Cụm 4 Thẻ KPI Tiến Độ SX */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiStatCard
              title="Kế hoạch sản xuất"
              value={prodSummary.totalPlan}
              unit="sp"
              formatType="number"
              colorVariant="blue"
              icon={Layers}
              badgeText={`${prodSummary.totalPOs} lệnh`}
              direction="positive"
              isLoading={isLoadingProd}
              testId="prod-kpi-plan"
            />

            <KpiStatCard
              title="Thực đạt KCS"
              value={prodSummary.totalProduced}
              prevValue={prodSummary.prevProduced}
              deltaPct={prodSummary.producedGrowth?.pct}
              diff={prodSummary.producedGrowth?.diff}
              unit="sp"
              formatType="number"
              colorVariant="emerald"
              icon={CheckCircle2}
              direction="positive"
              isLoading={isLoadingProd}
              testId="prod-kpi-ok"
            />

            <KpiStatCard
              title="Phế phẩm phát sinh"
              value={prodSummary.totalWaste}
              unit="sp"
              formatType="number"
              colorVariant="rose"
              icon={AlertTriangle}
              badgeText={`Tỷ lệ ${prodSummary.scrapRate}%`}
              direction="inverse"
              isLoading={isLoadingProd}
              testId="prod-kpi-scrap"
            />

            <KpiStatCard
              title="Tỷ lệ hoàn thành (Yield)"
              value={prodSummary.yieldRate}
              prevValue={prodSummary.prevYieldRate}
              deltaPct={prodSummary.yieldGrowth?.pct}
              formatType="percent"
              colorVariant="indigo"
              icon={Gauge}
              direction="positive"
              isLoading={isLoadingProd}
              testId="prod-kpi-yield"
            />
          </div>

          {/* Biểu Đồ Sản Lượng Theo Thiết Bị */}
          <Card className="shadow-2xs border-border bg-card">
            <CardHeader className="py-2.5 px-3.5 border-b border-border">
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-primary" />
                Sản Lượng Thực Đạt vs Phế Phẩm Theo Máy
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3.5">
              {isLoadingProd ? (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Đang tải biểu đồ...
                </div>
              ) : (prodReport?.outputByMachine || []).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8 font-medium">
                  Chưa có dữ liệu nhật ký máy trong kỳ
                </p>
              ) : (
                <div className="h-[220px] w-full">
                  <ResponsiveContainer width="100%" height={220} minWidth={1} minHeight={1}>
                    <BarChart
                      data={prodReport.outputByMachine}
                      margin={{ top: 10, right: 15, left: -10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="name" fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                      <YAxis fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                      <RechartsTooltip
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }}
                      />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="qtyOk" name="Đạt KCS" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                      <Bar dataKey="qtyNg" name="Phế phẩm" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bảng Kê Chi Tiết Lệnh Sản Xuất */}
          <Card className="shadow-2xs border-border bg-card overflow-hidden">
            <CardHeader className="flex flex-row justify-between items-center py-2.5 px-3.5 border-b border-border bg-muted/20">
              <div>
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                  Chi Tiết Lệnh Sản Xuất ({prodTotal.toLocaleString()} lệnh)
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={handleExportProduction}
                disabled={prodPOs.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 h-7 text-xs font-semibold shadow-2xs"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> Xuất Excel
              </Button>
            </CardHeader>

            <CardContent className="p-0">
              {isLoadingProd ? (
                <div className="flex justify-center items-center py-16 gap-2 text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin" /> Đang tải tiến độ lệnh sản xuất...
                </div>
              ) : prodPOs.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-xs">
                  Không tìm thấy lệnh sản xuất nào trong khoảng thời gian đã chọn.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/40">
                      <TableRow>
                        <TableHead className="w-12 text-center text-xs">STT</TableHead>
                        <TableHead className="text-xs">Mã LSX</TableHead>
                        <TableHead className="text-xs">Sản Phẩm</TableHead>
                        <TableHead className="text-xs">Khách Hàng</TableHead>
                        <TableHead className="text-xs text-right">Kế Hoạch</TableHead>
                        <TableHead className="text-xs text-right">Thực Đạt</TableHead>
                        <TableHead className="text-xs text-right">Phế Phẩm</TableHead>
                        <TableHead className="text-xs text-right">Tỷ Lệ Đạt</TableHead>
                        <TableHead className="text-xs text-center">Trạng Thái</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prodPOs.map((po: any, idx: number) => {
                        const plan = Number(po.qtyPlan || po.quantity || 0);
                        const ok = Number(po.qtyProduced || po.qtyOk || 0);
                        const ng = Number(po.qtyWaste || po.qtyNg || 0);
                        const yieldVal = plan > 0 ? Math.round((ok / plan) * 100) : (ok > 0 ? 100 : 0);
                        const stt = (prodPage - 1) * prodPageSize + idx + 1;

                        return (
                          <TableRow
                            key={po.id}
                            className="hover:bg-primary/5 cursor-pointer text-xs transition-colors"
                            onClick={(e) => handleRowClick(e, "productionOrder", po.id)}
                          >
                            <TableCell className="text-center font-mono text-muted-foreground">
                              {stt}
                            </TableCell>
                            <TableCell className="font-mono font-bold text-foreground">
                              {po.code || po.orderId}
                            </TableCell>
                            <TableCell className="font-semibold text-foreground">
                              {po.productName || po.product?.name || "Bao bì"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {po.customer?.shortName || po.customer?.name || "Khách hàng"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-foreground">
                              {plan.toLocaleString("vi-VN")}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-emerald-600">
                              {ok.toLocaleString("vi-VN")}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-rose-600">
                              {ng > 0 ? ng.toLocaleString("vi-VN") : "-"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-foreground">
                              {yieldVal}%
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-[10px]">
                                {po.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Phân Trang ERPPagination */}
              <ERPPagination
                currentPage={prodPage}
                totalPages={Math.max(1, Math.ceil(prodTotal / prodPageSize))}
                totalItems={prodTotal}
                pageSize={prodPageSize}
                onPageChange={setProdPage}
                onPageSizeChange={setProdPageSize}
                unitLabel="lệnh sản xuất"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 3: SỔ KHO XUẤT - NHẬP - TỒN (INVENTORY NXT) */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="inventory" className="space-y-3 mt-3">
          {/* Cụm 4 Thẻ KPI Kho Vận */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiStatCard
              title="Định giá vốn kho"
              value={invSummary.totalInventoryValue}
              prevValue={invSummary.prevInventoryValue}
              deltaPct={invSummary.valueGrowth?.pct}
              diff={invSummary.valueGrowth?.diff}
              formatType="currencyShort"
              colorVariant="emerald"
              icon={DollarSign}
              direction="positive"
              isLoading={isLoadingInv}
              testId="inv-kpi-val"
            />

            <KpiStatCard
              title="Tổng lượng tồn kho"
              value={invSummary.totalStockQty}
              unit="đv"
              formatType="number"
              colorVariant="blue"
              icon={Warehouse}
              badgeText={`${invSummary.totalItems} SKU`}
              direction="positive"
              isLoading={isLoadingInv}
              testId="inv-kpi-stock"
            />

            <KpiStatCard
              title="Nhập trong kỳ"
              value={invSummary.totalImportQty}
              unit="đv"
              formatType="number"
              colorVariant="teal"
              icon={CheckCircle2}
              direction="positive"
              isLoading={isLoadingInv}
              testId="inv-kpi-in"
            />

            <KpiStatCard
              title="Xuất trong kỳ"
              value={invSummary.totalExportQty}
              unit="đv"
              formatType="number"
              colorVariant="amber"
              icon={Package}
              direction="positive"
              isLoading={isLoadingInv}
              testId="inv-kpi-out"
            />
          </div>

          {/* Biểu Đồ Cơ Cấu Giá Trị Tồn Kho Theo Kho */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="lg:col-span-2 shadow-2xs border-border bg-card">
              <CardHeader className="py-2.5 px-3.5 border-b border-border">
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-primary" />
                  Giá Trị Tồn Kho Theo Từng Kho Hàng
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3.5">
                {isLoadingInv ? (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground text-xs">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Đang tải biểu đồ kho...
                  </div>
                ) : (invReport?.stockByWarehouse || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8 font-medium">
                    Chưa có số dư kho
                  </p>
                ) : (
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height={220} minWidth={1} minHeight={1}>
                      <BarChart
                        data={invReport.stockByWarehouse}
                        margin={{ top: 10, right: 15, left: -10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="name" fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                        <YAxis fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                        <RechartsTooltip
                          contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }}
                          formatter={(v: any) => [formatShortVnd(Number(v)), "Định giá vốn"]}
                        />
                        <Bar dataKey="totalValue" name="Giá trị vốn" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-2xs border-border bg-card flex flex-col">
              <CardHeader className="py-2.5 px-3.5 border-b border-border">
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
                  <PieIcon className="w-3.5 h-3.5 text-emerald-500" />
                  Cơ Cấu Vốn Kho
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3.5 flex-1 flex flex-col justify-center">
                {(invReport?.stockByWarehouse || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8 font-medium">
                    Không có số liệu
                  </p>
                ) : (
                  <RDonutChart
                    data={(invReport?.stockByWarehouse || []).map((wh: any, i: number) => ({
                      label: wh.name || wh.code,
                      value: wh.totalValue || 0,
                      color: CHART_PALETTE[i % CHART_PALETTE.length],
                    }))}
                    totalLabel="Tổng vốn kho"
                    height={220}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bảng Kê Sổ Kho NXT Có Phân Trang */}
          <Card className="shadow-2xs border-border bg-card overflow-hidden">
            <CardHeader className="flex flex-row justify-between items-center py-2.5 px-3.5 border-b border-border bg-muted/20">
              <div>
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                  Chi Tiết Sổ Kho NXT ({invTotal.toLocaleString()} mục)
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={handleExportInventory}
                disabled={invItems.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 h-7 text-xs font-semibold shadow-2xs"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> Xuất Sổ Kho
              </Button>
            </CardHeader>

            <CardContent className="p-0">
              {isLoadingInv ? (
                <div className="flex justify-center items-center py-16 gap-2 text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin" /> Đang tổng hợp sổ kho...
                </div>
              ) : invItems.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-xs">
                  Không tìm thấy mặt hàng nào trong kho.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/40">
                      <TableRow>
                        <TableHead className="w-12 text-center text-xs">STT</TableHead>
                        <TableHead className="text-xs">Mã Mặt Hàng</TableHead>
                        <TableHead className="text-xs">Tên Vật Tư / Sản Phẩm</TableHead>
                        <TableHead className="text-xs">Kho</TableHead>
                        <TableHead className="text-xs text-right">Đầu Kỳ</TableHead>
                        <TableHead className="text-xs text-right text-emerald-600">Nhập</TableHead>
                        <TableHead className="text-xs text-right text-rose-600">Xuất</TableHead>
                        <TableHead className="text-xs text-right font-bold">Cuối Kỳ</TableHead>
                        <TableHead className="text-xs text-right">Định Giá (VND)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invItems.map((item: any, idx: number) => {
                        const opening = Number(item.openingQty || 0);
                        const inQty = Number(item.inQty || 0);
                        const outQty = Number(item.outQty || 0);
                        const closing = Number(item.closingQty || 0);
                        const valuation = Number(item.valuation || 0);
                        const stt = (invPage - 1) * invPageSize + idx + 1;

                        return (
                          <TableRow
                            key={item.id || idx}
                            className="hover:bg-primary/5 cursor-pointer text-xs transition-colors"
                            onClick={(e) => handleRowClick(e, item.itemType === "PRODUCT" || item.itemType === "FG" ? "product" : "material", item.itemId)}
                          >
                            <TableCell className="text-center font-mono text-muted-foreground">
                              {stt}
                            </TableCell>
                            <TableCell className="font-mono font-bold text-foreground">
                              {item.itemCode || item.itemId}
                            </TableCell>
                            <TableCell className="font-semibold text-foreground">
                              {item.itemName}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {item.warehouseName || "-"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {opening.toLocaleString("vi-VN")}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-emerald-600">
                              {inQty > 0 ? inQty.toLocaleString("vi-VN") : "-"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-rose-600">
                              {outQty > 0 ? outQty.toLocaleString("vi-VN") : "-"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-foreground">
                              {closing.toLocaleString("vi-VN")}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-foreground">
                              {formatCurrencyVND(valuation)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Phân Trang ERPPagination */}
              <ERPPagination
                currentPage={invPage}
                totalPages={Math.max(1, Math.ceil(invTotal / invPageSize))}
                totalItems={invTotal}
                pageSize={invPageSize}
                onPageChange={setInvPage}
                onPageSizeChange={setInvPageSize}
                unitLabel="mặt hàng"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 4: PHÂN TÍCH TUỔI NỢ (AR AGING) */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="ar_aging" className="space-y-3 mt-3">
          {/* Cụm 4 Thẻ KPI Tuổi Nợ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiStatCard
              title="Tổng công nợ AR"
              value={arSummary.totalDebt}
              formatType="currencyShort"
              colorVariant="purple"
              icon={CreditCard}
              badgeText={`${arSummary.totalCustomersWithDebt} KH nợ`}
              direction="inverse"
              isLoading={isLoadingAR}
              testId="ar-kpi-total"
            />

            <KpiStatCard
              title="Trong hạn (0-30 ngày)"
              value={arSummary.inTermDebt}
              formatType="currencyShort"
              colorVariant="emerald"
              icon={Clock}
              badgeText="Dòng tiền an toàn"
              direction="positive"
              isLoading={isLoadingAR}
              testId="ar-kpi-in-term"
            />

            <KpiStatCard
              title="Nợ quá hạn (>30 ngày)"
              value={arSummary.overdueDebt}
              formatType="currencyShort"
              colorVariant="rose"
              icon={AlertTriangle}
              badgeText={`Tỷ lệ ${arSummary.overdueRate}%`}
              direction="inverse"
              isLoading={isLoadingAR}
              testId="ar-kpi-overdue"
            />

            <KpiStatCard
              title="Khách hàng có dư nợ"
              value={arSummary.totalCustomersWithDebt}
              unit="KH"
              formatType="number"
              colorVariant="amber"
              icon={Clock}
              badgeText="Đang theo dõi"
              direction="inverse"
              isLoading={isLoadingAR}
              testId="ar-kpi-cust"
            />
          </div>

          {/* Biểu Đồ Tháp Tuổi Nợ (Aging Buckets) */}
          <Card className="shadow-2xs border-border bg-card">
            <CardHeader className="py-2.5 px-3.5 border-b border-border">
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-purple-600" />
                Tháp Tuổi Nợ Theo 4 Dải Thời Gian
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3.5">
              {isLoadingAR ? (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Đang tính toán tuổi nợ...
                </div>
              ) : (arReport?.agingBuckets || []).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8 font-medium">
                  Không có công nợ quá hạn
                </p>
              ) : (
                <div className="h-[200px] w-full">
                  <ResponsiveContainer width="100%" height={200} minWidth={1} minHeight={1}>
                    <BarChart
                      data={arReport.agingBuckets}
                      margin={{ top: 10, right: 15, left: -10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="range" fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} />
                      <YAxis fontSize={11} stroke="#64748b" tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                      <RechartsTooltip
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }}
                        formatter={(v: any) => [formatShortVnd(Number(v)), "Dư nợ"]}
                      />
                      <Bar dataKey="amount" name="Dư nợ" radius={[4, 4, 0, 0]} barSize={32}>
                        {arReport.agingBuckets.map((b: any, i: number) => {
                          const fills = ["#10b981", "#f59e0b", "#f97316", "#ef4444"];
                          return <Cell key={i} fill={fills[i % fills.length]} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bảng Kê Chi Tiết Tuổi Nợ */}
          <Card className="shadow-2xs border-border bg-card overflow-hidden">
            <CardHeader className="flex flex-row justify-between items-center py-2.5 px-3.5 border-b border-border bg-muted/20">
              <div>
                <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                  Chi Tiết Tuổi Nợ Khách Hàng ({arItems.length} khách hàng)
                </CardTitle>
              </div>
              <Button
                size="sm"
                onClick={handleExportARAging}
                disabled={arItems.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5 h-7 text-xs font-semibold shadow-2xs"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> Xuất Excel Tuổi Nợ
              </Button>
            </CardHeader>

            <CardContent className="p-0">
              {isLoadingAR ? (
                <div className="flex justify-center items-center py-16 gap-2 text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin" /> Đang tổng hợp phân tích tuổi nợ...
                </div>
              ) : arItems.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-xs">
                  Không có dữ liệu công nợ khách hàng.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/40">
                      <TableRow>
                        <TableHead className="w-12 text-center text-xs">STT</TableHead>
                        <TableHead className="text-xs">Mã KH</TableHead>
                        <TableHead className="text-xs">Tên Khách Hàng</TableHead>
                        <TableHead className="text-xs text-right">Tổng Dư Nợ</TableHead>
                        <TableHead className="text-xs text-right text-emerald-600">0 - 30 Ngày</TableHead>
                        <TableHead className="text-xs text-right text-amber-600">31 - 60 Ngày</TableHead>
                        <TableHead className="text-xs text-right text-orange-600">61 - 90 Ngày</TableHead>
                        <TableHead className="text-xs text-right text-rose-600">&gt; 90 Ngày</TableHead>
                        <TableHead className="text-xs text-center">Đánh Giá</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {arItems
                        .slice((arPage - 1) * arPageSize, arPage * arPageSize)
                        .map((row: any, idx: number) => {
                          const total = Number(row.totalOutstanding || row.totalDebt || 0);
                          const current = Number(row.current || 0);
                          const o30 = Number(row.overdue30 || 0);
                          const o60 = Number(row.overdue60 || 0);
                          const o90 = Number(row.overdue90 || 0);
                          const stt = (arPage - 1) * arPageSize + idx + 1;

                          return (
                            <TableRow
                              key={row.customerId || idx}
                              className="hover:bg-primary/5 cursor-pointer text-xs transition-colors"
                              onClick={(e) => handleRowClick(e, "customer", row.customerId)}
                            >
                              <TableCell className="text-center font-mono text-muted-foreground">
                                {stt}
                              </TableCell>
                              <TableCell className="font-mono font-bold text-foreground">
                                {row.customerCode || "-"}
                              </TableCell>
                              <TableCell className="font-semibold text-foreground">
                                {row.customerName || "Khách hàng"}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-foreground">
                                {formatCurrencyVND(total)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-emerald-600 font-medium">
                                {current > 0 ? formatCurrencyVND(current) : "-"}
                              </TableCell>
                              <TableCell className="text-right font-mono text-amber-600 font-medium">
                                {o30 > 0 ? formatCurrencyVND(o30) : "-"}
                              </TableCell>
                              <TableCell className="text-right font-mono text-orange-600 font-medium">
                                {o60 > 0 ? formatCurrencyVND(o60) : "-"}
                              </TableCell>
                              <TableCell className="text-right font-mono text-rose-600 font-bold">
                                {o90 > 0 ? formatCurrencyVND(o90) : "-"}
                              </TableCell>
                              <TableCell className="text-center">
                                {o90 > 0 ? (
                                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                                    Nguy cơ xấu
                                  </Badge>
                                ) : o60 > 0 ? (
                                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]">
                                    Cảnh báo cao
                                  </Badge>
                                ) : o30 > 0 ? (
                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                                    Cần nhắc
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                    An toàn
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Phân Trang ERPPagination */}
              <ERPPagination
                currentPage={arPage}
                totalPages={Math.max(1, Math.ceil(arItems.length / arPageSize))}
                totalItems={arItems.length}
                pageSize={arPageSize}
                onPageChange={setArPage}
                onPageSizeChange={setArPageSize}
                unitLabel="khách hàng"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 5: NHẬT KÝ XƯỞNG (SHOPFLOOR LOGS) */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="shopfloor" className="space-y-3 mt-3">
          <Card className="shadow-2xs border-border bg-card">
            <CardHeader className="py-2.5 px-3.5 border-b border-border">
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                Nhật Ký Thao Tác Máy Xưởng In & Gia Công
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3.5">
              {isLoadingLogs ? (
                <div className="flex justify-center items-center py-16 gap-2 text-muted-foreground text-xs">
                  <Loader2 className="w-5 h-5 animate-spin" /> Đang tải nhật ký xưởng...
                </div>
              ) : (
                <div className="text-xs text-muted-foreground py-4 font-medium">
                  Đã ghi nhận {(logsRes?.items || []).length} lượt nhật ký máy trong kỳ.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ZIndexView
        open={zIndex.open}
        onOpenChange={zIndex.onOpenChange}
        type={zIndex.type}
        id={zIndex.id}
      />
    </div>
  );
}
